import { useEffect, useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MangaCard from '@/components/base/MangaCard';
import { mangas, chapterPages } from '@/mocks/mangaData';

function ChapterContent() {
  return (
    <div className="w-full max-w-[800px] mx-auto">
      {chapterPages.map((page, index) => (
        <div key={index} className="w-full">
          <img
            src={page}
            alt={`健身教练漫画 - 第 ${index + 1} 页`}
            title={`健身教练漫画免费下拉式阅读 - 第 ${index + 1} 页`}
            className="w-full h-auto block"
            loading={index === 0 ? 'eager' : 'lazy'}
          />
        </div>
      ))}
    </div>
  );
}

export default function ArticlePage() {
  const { mangaId, chapterId } = useParams<{ mangaId: string; chapterId: string }>();
  const [currentChapter, setCurrentChapter] = useState(Number(chapterId) || 1);

  const manga = useMemo(() => mangas.find((m) => m.id === Number(mangaId)), [mangaId]);

  const chapterInfo = useMemo(() => {
    if (!manga) return null;
    return manga.chapters_list.find((c) => c.id === currentChapter) || manga.chapters_list[0];
  }, [manga, currentChapter]);

  const prevChapter = useMemo(() => {
    if (!manga) return null;
    const idx = manga.chapters_list.findIndex((c) => c.id === currentChapter);
    return idx > 0 ? manga.chapters_list[idx - 1] : null;
  }, [manga, currentChapter]);

  const nextChapter = useMemo(() => {
    if (!manga) return null;
    const idx = manga.chapters_list.findIndex((c) => c.id === currentChapter);
    return idx < manga.chapters_list.length - 1 ? manga.chapters_list[idx + 1] : null;
  }, [manga, currentChapter]);

  const relatedMangas = useMemo(() => {
    if (!manga) return [];
    return mangas.filter((m) => m.category === manga.category && m.id !== manga.id).slice(0, 4);
  }, [manga]);

  useEffect(() => {
    if (mangaId) setCurrentChapter(Number(chapterId) || 1);
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [mangaId, chapterId]);

  useEffect(() => {
    const titleText = manga && chapterInfo
      ? `${manga.title} ${chapterInfo.title} - 免费下拉式阅读 - 4399漫画网`
      : '漫画阅读 - 4399漫画网';
    document.title = titleText;

    const desc = manga
      ? `4399漫画网免费下拉式阅读${manga.title}${chapterInfo?.title || ''}，极速加载，无缝翻页，畅享最流畅的漫画阅读体验。`
      : '4399漫画网免费下拉式阅读漫画，极速加载，无缝翻页。';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', desc);

    const metaKw = document.querySelector('meta[name="keywords"]');
    if (metaKw) metaKw.setAttribute('content', `4399漫画,下拉式阅读,免费漫画,${manga?.title || '漫画'}`);

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', `http://chigua52.cn/manga/${mangaId}/chapter/${currentChapter}`);
  }, [manga, chapterInfo, mangaId, currentChapter]);

  if (!manga) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="flex flex-col items-center justify-center py-40 px-4">
          <i className="ri-emotion-sad-line text-5xl text-foreground-300 mb-4"></i>
          <h1 className="text-xl font-semibold text-foreground-700 mb-2">漫画未找到</h1>
          <p className="text-sm text-foreground-400 mb-6">该漫画不存在或已下架</p>
          <Link to="/" className="bg-primary-500 text-white px-6 py-2.5 rounded-full text-sm cursor-pointer">返回首页</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const breadcrumbItems = [
    { label: '首页', path: '/' },
    { label: `${manga.category === 'sports' ? '运动竞技' : manga.category}漫画`, path: `/category/${manga.category}` },
    { label: manga.title, path: `/manga/${manga.id}/chapter/${manga.chapters}` },
    { label: chapterInfo?.title || '' },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    'headline': `${manga.title} - ${chapterInfo?.title || ''}`,
    'author': { '@type': 'Person', 'name': manga.author },
    'datePublished': chapterInfo?.date || manga.updatedAt,
    'publisher': { '@type': 'Organization', 'name': '4399漫画网' },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': [
      { '@type': 'ListItem', 'position': 1, 'name': '首页', 'item': 'http://chigua52.cn/' },
      { '@type': 'ListItem', 'position': 2, 'name': `${manga.category === 'sports' ? '运动竞技' : manga.category}漫画`, 'item': `http://chigua52.cn/category/${manga.category}` },
      { '@type': 'ListItem', 'position': 3, 'name': manga.title, 'item': `http://chigua52.cn/manga/${manga.id}/chapter/${manga.chapters}` },
      { '@type': 'ListItem', 'position': 4, 'name': chapterInfo?.title || '' },
    ],
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <Breadcrumb items={breadcrumbItems} />

        <header className="relative w-full h-[280px] md:h-[380px] flex flex-col items-center justify-center mb-0 overflow-hidden">
          <img
            src={manga.cover}
            alt={`${manga.title} - 4399漫画网`}
            title={`${manga.title} - 4399漫画网免费下拉式阅读`}
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/60"></div>
          <div className="relative z-10 text-center px-4 w-full max-w-2xl mx-auto">
            <h1 className="text-xl md:text-3xl font-bold text-white font-heading">{manga.title}</h1>
            {chapterInfo && (
              <h2 className="mt-2 text-base md:text-lg text-white/80">{chapterInfo.title}</h2>
            )}
            <p className="mt-3 text-xs md:text-sm text-white/60 max-w-md mx-auto">
              作者：{manga.author} · 状态：{manga.status} · 共 {manga.chapters} 话
            </p>
            <div className="mt-4 flex items-center justify-center gap-3">
              <span className="text-xs text-white/70 bg-white/15 px-3 py-1 rounded-full">
                下拉式阅读中...
              </span>
            </div>
          </div>
        </header>

        <main>
          <div className="flex items-center justify-between px-4 md:px-6 py-3 mx-auto max-w-[800px] bg-background-100 border-b border-background-200/70 sticky top-16 z-40">
            <div className="flex items-center gap-2">
              {prevChapter ? (
                <Link
                  to={`/manga/${manga.id}/chapter/${prevChapter.id}`}
                  className="flex items-center gap-1 text-xs text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer px-3 py-1.5 rounded-full bg-background-50 border border-background-200/70 whitespace-nowrap"
                >
                  <i className="ri-arrow-left-s-line"></i> 上一话
                </Link>
              ) : (
                <span className="flex items-center gap-1 text-xs text-foreground-300 px-3 py-1.5 rounded-full bg-background-50 border border-background-200/70 whitespace-nowrap cursor-default">
                  <i className="ri-arrow-left-s-line"></i> 上一话
                </span>
              )}
              {nextChapter && (
                <Link
                  to={`/manga/${manga.id}/chapter/${nextChapter.id}`}
                  className="flex items-center gap-1 text-xs text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer px-3 py-1.5 rounded-full bg-background-50 border border-background-200/70 whitespace-nowrap"
                >
                  下一话 <i className="ri-arrow-right-s-line"></i>
                </Link>
              )}
            </div>
            <select
              value={currentChapter}
              onChange={(e) => {
                const val = Number(e.target.value);
                setCurrentChapter(val);
                window.REACT_APP_NAVIGATE(`/manga/${manga.id}/chapter/${val}`);
              }}
              className="text-xs bg-background-50 border border-background-200/70 rounded-full px-3 py-1.5 text-foreground-700 outline-none cursor-pointer"
            >
              {manga.chapters_list.map((ch) => (
                <option key={ch.id} value={ch.id}>{ch.title}</option>
              ))}
            </select>
          </div>

          <section className="py-0">
            <ChapterContent />
          </section>

          <div className="flex items-center justify-center gap-3 px-4 md:px-6 py-6 mx-auto max-w-[800px]">
            {prevChapter ? (
              <Link
                to={`/manga/${manga.id}/chapter/${prevChapter.id}`}
                className="flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white px-6 py-2.5 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i> 上一话
              </Link>
            ) : (
              <span className="flex items-center gap-2 bg-foreground-300 text-white px-6 py-2.5 rounded-full text-sm font-medium cursor-default whitespace-nowrap">
                <i className="ri-arrow-left-line"></i> 上一话
              </span>
            )}
            {nextChapter ? (
              <Link
                to={`/manga/${manga.id}/chapter/${nextChapter.id}`}
                className="flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white px-6 py-2.5 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                下一话 <i className="ri-arrow-right-line"></i>
              </Link>
            ) : (
              <span className="flex items-center gap-2 bg-foreground-300 text-white px-6 py-2.5 rounded-full text-sm font-medium cursor-default whitespace-nowrap">
                下一话 <i className="ri-arrow-right-line"></i>
              </span>
            )}
          </div>

          <section className="py-10 md:py-16 px-4 md:px-6 mx-auto max-w-7xl">
            <h3 className="text-lg md:text-xl font-bold text-foreground-950 font-heading mb-6">
              猜你喜欢
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {relatedMangas.map((m) => (
                <MangaCard key={m.id} {...m} />
              ))}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}